import React, { useCallback, useEffect, useState } from 'react';
import OrganizationPendingInvitations from '../OrganizationPendingInvitations';
import { Plus, Trash2 } from 'lucide-react';
import UserAvatar from '../ui/UserAvatar';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { ConfirmModal } from '../ui/Modal';
import organizationsService from '../../services/organizationsService';

/**
 * The organisations surface (plan §5, Phase 4b).
 *
 * Deliberately in the same visual language as Teams: an organisation should
 * feel like a team's PARENT, not a different product. The one place it
 * diverges is the header — a banner, an overlapping logo and social links —
 * because that is a company profile, and it is the same header Phase 6 renders
 * publicly (see OrganizationHeader).
 *
 * Everything here is read-then-write against the API. There is no local
 * mutation of the store ahead of a response: an organisation is administered
 * rarely and by few people, so optimism buys nothing and costs a UI that can
 * disagree with the server about who is an admin.
 */

export const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
];

// ── Members ──────────────────────────────────────────────────────────────────

export function MembersTab({ org, canEdit, currentUserId }) {
  // Bumped after a successful invite so the pending list re-fetches; without
  // it a just-sent invitation would not appear until the tab was reopened.
  const [sentCount, setSentCount] = useState(0);
  const [members, setMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null);

  const load = useCallback(async () => {
    try {
      setMembers(await organizationsService.getMembers(org.id));
    } catch (e) {
      setError(e.message);
    }
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  const changeRole = async (userId, role) => {
    setError('');
    try {
      await organizationsService.updateMemberRole(org.id, userId, role);
      await load();
    } catch (e) {
      // 409 is the last-owner guard: the caller is allowed, the organisation
      // is in a state that refuses. Saying which is the difference between
      // "you can't" and "promote someone else first".
      setError(
        e?.response?.status === 409
          ? 'An organization must keep at least one owner.'
          : 'Could not change that role.'
      );
    }
  };

  const remove = async (userId) => {
    setError('');
    try {
      await organizationsService.removeMember(org.id, userId);
      await load();
    } catch (e) {
      setError(
        e?.response?.status === 409
          ? 'An organization must keep at least one owner.'
          : 'Could not remove that member.'
      );
    }
  };

  const invite = async () => {
    const address = inviteEmail.trim();
    if (!address) return;
    setError('');
    try {
      const res = await organizationsService.invite(org.id, address);
      setInviteEmail('');
      await load();
      setSentCount((n) => n + 1);
      // Which of the two happened, said plainly. "They're in" and "we sent an
      // email" are different things to be waiting for.
      setNotice(
        res.result === 'added'
          ? `${address} was added.`
          : `Invitation emailed to ${address}.`
      );
    } catch (e) {
      // 404 is the address, not the organisation. Saying which is the
      // difference between "check the spelling" and "something broke".
      setError(
        e?.response?.data?.errors?.organization_id?.[0] ||
          e?.response?.data?.errors?.email?.[0] ||
          'Could not invite that person.'
      );
    }
  };

  return (
    <div className="org-list">
      {canEdit && (
        <div className="org-invite">
          <input
            className="org-input"
            type="email"
            value={inviteEmail}
            placeholder="Add someone by email"
            aria-label="Add a member by email"
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && invite()}
          />
          <button type="button" className="org-btn" disabled={!inviteEmail.trim()} onClick={invite}>
            <Plus size={13} /> Add
          </button>
        </div>
      )}
      {/* Both halves are true now. An address that already has an account is
          added outright; one that does not is emailed an invitation and shows
          up under "Pending invitations" below until they take it up. */}
      {canEdit && (
        <p className="org-hint">
          If they already have an Apiboo account they're added straight away. If not, we email
          them an invitation.
        </p>
      )}
      {notice && <p className="org-hint">{notice}</p>}
      {error && <p className="org-error">{error}</p>}
      {members.map((m) => (
        <div key={m.id} className="org-row">
          <UserAvatar userId={m.id} name={m.name} size={28} className="org-row-avatar" />
          <div className="org-row-texts">
            <span className="org-row-name">
              {String(m.id) === String(currentUserId) ? 'Me' : m.name || m.email}
            </span>
            <span className="org-row-sub">{m.email}</span>
          </div>
          {canEdit ? (
            <CompactSelectPicker
              className="org-role-picker"
              value={m.role}
              options={ROLE_OPTIONS}
              aria-label={`Role for ${m.name || m.email}`}
              onChange={(role) => changeRole(m.id, role)}
            />
          ) : (
            <span className="org-row-role">{m.role}</span>
          )}
          {canEdit && (
            <button
              type="button"
              className="org-icon-btn"
              aria-label={`Remove ${m.name || m.email}`}
              onClick={() => setConfirmRemove(m)}
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      ))}

      {/* Below the members, never mixed into them: a pending invitation is not
          a member, and blurring the two makes the seat count look wrong to
          whoever is paying for the seats. */}
      <OrganizationPendingInvitations orgId={org.id} canEdit={canEdit} refreshKey={sentCount} />

      <ConfirmModal
        isOpen={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={() => {
          const m = confirmRemove;
          setConfirmRemove(null);
          if (m) remove(m.id);
        }}
        title="Remove from organization?"
        message={
          confirmRemove
            ? `${confirmRemove.name || confirmRemove.email} loses administration of this organization's teams. Their own teams and data are untouched.`
            : ''
        }
        confirmText="Remove"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}
